<script setup>
import ToastComponent from '@/components/ToastComponent.vue';
import { useAxios } from '@/hooks/useAxios';
import { useCommonCodeStore } from '@/store/commoncode-store';
import { useReservationsStore } from '@/store/reservation-store';
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { useRoute } from 'vue-router';

const props = defineProps({
    contractType: {
        type: String,
        default: 'INSU'
    },
    replaceContractNo: {
        type: String,
        default: ''
    },
    validMsg: {
        type: Object,
        default: () => ({})
    },
    requiredList: {
        type: [Array, Object],
        default: () => []
    },
    readonlyList: {
        type: [Array, Object],
        default: () => []
    },
    hideList: {
        type: [Array, Object],
        default: () => []
    },
    reservationData: {
        type: Object,
        default: () => ({})
    },
    contractNo: {
        type: String,
        default: ''
    },
    vehicle: {
        type: Object,
        default: () => ({})
    }
});

const result_amount = ref(0);
const emit = defineEmits(['update']);
const route = useRoute();
const store = useReservationsStore();
const sessionKey = `${route.name}`;
const toastComponentRef = ref(null);
let referralList = ref([]);
const commonCodes = computed(() => useCommonCodeStore().getCommonCodes);
// const codes = computed(() => useCommonCodeStore().getCommonCodes)
// const getCommonCode = (code) => codes.value?.[code] ?? []
const commonCode = (code) => {
    const returnVal = computed(() => {
        return commonCodes.value[code];
    });
    return returnVal;
};

const generateArray = (length, unit) => {
    return Array.from({ length }, (_, i) => ({
        id: i + 1,
        value: i + 1,
        text: `${i + 1} ${unit}`
    }));
};

const initialData = {
    referral_id: '',
    formula: '',
    charge_ratio: '',
    total_amount: '',
    payment_amount: 0,
    difference_amount: 0,
    net_amount: ''
};

const data = reactive({ ...initialData });
const sessionRef = store.ensure(sessionKey);

const commonSyncKeys = computed(() => Object.keys(data).filter((k) => Object.prototype.hasOwnProperty.call(sessionRef, k)));
const pickKeys = (src, keys) =>
    keys.reduce((o, k) => {
        if (k in src) o[k] = src[k];
        return o;
    }, {});

let pushing = false;
let pulling = false;

const requiredData = [];
const requiredKeys = computed(() => {
    const res = props.requiredList;
    if (Array.isArray(res) && res.length) return res;
    if (res && typeof res === 'object' && Object.keys(res).length) {
        return Object.keys(res).filter((key) => !!res[key]);
    }
    return requiredData;
});

const requiredKeySet = computed(() => new Set(requiredKeys.value));
const isRequired = (key) => requiredKeySet.value.has(key);

const initialValidMessage = {
    referral_id: '',
    formula: '',
    charge_ratio: '',
    total_amount: '',
    payment_amount: '',
    difference_amount: '',
    net_amount: ''
};
const validationMessage = reactive({ ...initialValidMessage });

const allReadonly = ref(false);
const setAllReadonly = (val = true) => {
    allReadonly.value = !!val;
};
const allowedKeys = Object.keys(initialData);

const propsReadonlyEnforced = computed(() => {
    console.log(props.readonlyList);
    const res = props.readonlyList;
    const set = new Set();
    if (Array.isArray(res)) res.forEach((k) => set.add(k));
    else if (res && typeof res === 'object') {
        Object.keys(res).forEach((k) => {
            if (res[k]) set.add(k);
        });
    }
    return set;
});

const finalReadonlySet = computed(() => {
    const enforced = propsReadonlyEnforced.value;
    const result = new Set(enforced);

    if (allReadonly.value) {
        allowedKeys.forEach((k) => {
            if (!enforced.has(k)) result.add(k);
        });
    }

    if (props.replaceContractNo) {
        result.add('charge_ratio');
    }

    return result;
});

const isFieldReadonly = (key) => finalReadonlySet.value.has(key);
const isEffectivelyRequired = (key) => requiredKeySet.value.has(key) && !isFieldReadonly(key) && !isHidden(key);

const hideKeySet = computed(() => {
    const res = props.hideList;
    if (Array.isArray(res) && res.length) return new Set(res);
    if (res && typeof res === 'object' && Object.keys(res).length) {
        return new Set(Object.keys(res).filter((key) => !!res[key]));
    }
    return new Set();
});

const isHidden = (key) => hideKeySet.value.has(key);
const isTotalHidden = computed(() => isHidden('total_amount'));

const resetData = () => {
    Object.assign(data, initialData);
    Object.assign(validationMessage, initialValidMessage);
    store.update(sessionKey, pickKeys(data, commonSyncKeys.value));
};
const excludeReturn = new Set(['vehicle_model_name', 'fuel_type']);
const getData = () => {
    const payload = {};
    for (const key of allowedKeys) {
        if (excludeReturn.has(key)) continue;
        if (!isHidden(key)) payload[key] = data[key];
    }
    return payload;
};

const setReservationData = (newVal) => {
    if (!newVal) return;
    const payload = newVal.contract_data ?? newVal;

    Object.entries(payload).forEach(([key, val]) => {
        if (allowedKeys.includes(key)) {
            data[key] = val;
        }
    });
};

const checkRequired = () => {
    Object.keys(validationMessage).forEach((k) => (validationMessage[k] = ''));
    let result = true;

    const keysToCheck = new Set(requiredKeys.value.filter((key) => allowedKeys.includes(key) && isEffectivelyRequired(key)));

    for (const key of keysToCheck) {
        const val = data[key];
        const isEmpty = val === '' || val === null || val === undefined;
        const isIdInvalid = key.endsWith('_id') && (isEmpty || Number(val) === 0);
        if (isEmpty || isIdInvalid) {
            if (key in validationMessage) validationMessage[key] = '필수입력입니다.';
            result = false;
        }
    }
    return result;
};

const setErrorMessage = (payload, reset = false) => {
    if (!payload) return;

    if (reset) {
        Object.keys(validationMessage).forEach((k) => (validationMessage[k] = ''));
    }

    const collectPairs = (node, out = []) => {
        if (node == null) return out;

        if (Array.isArray(node)) {
            node.forEach((item) => collectPairs(item, out));
            return out;
        }

        if (typeof node === 'object') {
            for (const [k, v] of Object.entries(node)) {
                if (v && typeof v === 'object' && !Array.isArray(v)) {
                    collectPairs(v, out);
                } else {
                    out.push([k, v]);
                }
            }
            return out;
        }

        return out;
    };

    const pairs = collectPairs(payload);

    for (const [rawKey, rawVal] of pairs) {
        let key = String(rawKey).replace(/^contract_data\./, '');
        if (!(key in validationMessage)) continue;

        let msg = rawVal;
        if (Array.isArray(msg)) msg = msg.filter(Boolean).join(', ');
        if (msg == null) msg = '';

        validationMessage[key] = String(msg);
    }
};

const formatCurrency = (value) => {
    if (value === null || value === undefined || isNaN(Number(value))) {
        return '0';
    }
    return Number(value).toLocaleString();
};

const searchReferralList = async () => {
    const config = { method: 'get' };
    try {
        const { execute } = useAxios('/api/referral/normal', config, {
            immediate: false,
            onSuccess(response) {
                // console.log(response.data.data);

                const newList = response.data.data.map((item, index) => ({
                    value: item.referral_id,
                    text: item.referral_name,
                    id: item.referral_id,
                    title: item.referral_name
                }));
                referralList.value = newList;
            },
            onError(err) {
                console.error('Error:', err.message);
                referralList.value = [];
            }
        });
        await execute();
    } catch (err) {
        console.error(err);
        referralList.value = [];
    }
};

const numberFormatter = (n) => {
    const v = Number(n ?? 0);
    return isFinite(v) ? v.toLocaleString() : '0';
};

const formatDate = (date) => {
    if (!date) return '';
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
};

function handleInsuranceListCompleted(val) {
    console.log('청구기준요금 결과', val);
    data.formula = `${val.formula} * ${val.charge_ratio / 100} = ${(val.calculate_price * val.charge_ratio) / 100}`;
    data.difference_amount = val.calculate_price * (val.charge_ratio / 100);
    data.net_amount = val.calculate_price * (val.charge_ratio / 100);
    validationMessage.formula = '';
}

onMounted(() => {
    searchReferralList();
});

watch(
    () => props.reservationData,
    (newVal) => {
        if (!newVal || typeof newVal !== 'object') return;
        setReservationData(newVal);
    },
    { immediate: true, deep: false }
);

watch(
    () => props.readonlyList,
    () => {
        Object.keys(validationMessage).forEach((key) => {
            if (isFieldReadonly(key)) validationMessage[key] = '';
        });
        // checkRequired();
    },
    { immediate: false, deep: false }
);

watch(
    () => props.hideList,
    () => {
        Object.keys(validationMessage).forEach((key) => {
            if (isHidden(key)) validationMessage[key] = '';
        });
    },
    { immediate: false, deep: false }
);

watch(
    () => props.validMsg,
    (newVal) => {
        if (!newVal || typeof newVal !== 'object') {
            console.error('Invalid Error data');
            return;
        }
        setErrorMessage(newVal, true);
    },
    { immediate: true }
);

watch(
    () => commonSyncKeys.value.map((k) => data[k]),
    () => {
        if (pulling) return;
        pushing = true;
        store.update(sessionKey, pickKeys(data, commonSyncKeys.value));
        pushing = false;
    },
    { deep: false, immediate: true }
);

watch(
    () => commonSyncKeys.value.map((k) => sessionRef[k]),
    () => {
        if (pushing) return;
        pulling = true;
        commonSyncKeys.value.forEach((k) => {
            if (sessionRef[k] !== undefined && data[k] !== sessionRef[k]) {
                data[k] = sessionRef[k];
            }
        });
        pulling = false;
    },
    { deep: false, immediate: true }
);

defineExpose({
    getData,
    resetData,
    setReservationData,
    checkRequired,
    setAllReadonly,
    setErrorMessage
});
</script>

<template>
    <ToastComponent ref="toastComponentRef" />
    <div class="section-box base-text">
        <div class="section-body">
            <div class="section-left">
                <div class="flex items-center justify-between">
                    <div class="flex items-center">
                        <h2 class="section-title z-[-10] py-4">예약 정보</h2>
                    </div>
                </div>

                <div class="section-row">
                    <div v-if="!isHidden('referral_id')" :class="['input-box', isTotalHidden ? 'w-full' : 'w-[33%]']">
                        <combobox
                            inputId="referral_id"
                            :options="referralList"
                            v-model="data.referral_id"
                            :errmsg="validationMessage.referral_id"
                            label="소개처"
                            :readonly="isFieldReadonly('referral_id')"
                            :required="isEffectivelyRequired('referral_id')"
                            :placeholder="'소개처를 선택하세요.'"
                        />
                    </div>
                    <div v-if="!isHidden('formula')" class="input-box w-[22.5%]">
                        <inputbox inputId="formula" v-model="data.formula" :errmsg="validationMessage.formula" label="청구수식" :readonly="true" :required="isEffectivelyRequired('formula')" />
                    </div>
                    <div v-if="!isHidden('charge_ratio')" class="input-box w-[10%]">
                        <combobox
                            inputId="charge_ratio"
                            :options="generateArray(100, '%')"
                            v-model="data.charge_ratio"
                            :errmsg="validationMessage.charge_ratio"
                            label="청구율"
                            :readonly="isFieldReadonly('charge_ratio')"
                            :required="isEffectivelyRequired('charge_ratio')"
                            :placeholder="'청구율을 선택하세요.'"
                        />
                    </div>
                    <div v-if="!isHidden('net_amount')" class="input-box w-[33%]">
                        <insurancerentallist
                            inputId="total_amount"
                            v-model="data.total_amount"
                            :errmsg="validationMessage.total_amount"
                            :vehicle="props.vehicle"
                            :contractNo="props.contractNo"
                            :chargeRatio="data.charge_ratio"
                            label="청구기준요금"
                            :placeholder="'선택 또는 입력하세요.'"
                            :maxlength="11"
                            :readonly="isFieldReadonly('net_amount')"
                            :required="isEffectivelyRequired('net_amount')"
                            @input="(payload) => handleInsuranceListCompleted(payload)"
                        />
                    </div>
                </div>
                <div class="section-row">
                    <div class="input-box w-[33%]">
                        <inputbox inputId="net_amount" v-model="data.net_amount" :number="'n'" :unit="'원'" :errmsg="validationMessage.net_amount" label="청구 금액" :maxlength="15" :readonly="true" />
                    </div>
                    <div v-if="!isHidden('payment_amount')" class="input-box w-[33%]">
                        <inputbox inputId="payment_amount" v-model="data.payment_amount" :number="'n'" :allowZero="true" :unit="'원'" :errmsg="validationMessage.payment_amount" label="총 입금액" :maxlength="12" :readonly="true" />
                    </div>
                    <div v-if="!isHidden('difference_amount')" class="input-box w-[33%]">
                        <inputbox inputId="difference_amount" v-model="data.difference_amount" :number="'n'" :allowZero="true" :unit="'원'" :errmsg="validationMessage.difference_amount" :label="'차액'" :maxlength="12" :readonly="true" />
                    </div>
                    <!-- <div v-if="!isHidden('net_amount')" class="input-box w-[33%]">
                        <inputbox inputId="net_amount" v-model="data.net_amount" :number="'n'" :unit="'원'" :errmsg="validationMessage.net_amount" label="총 요금" :maxlength="15" :readonly="true" />
                    </div> -->
                </div>
            </div>
        </div>
    </div>
</template>
<style src="@/assets/css/form-common.css"></style>

<style scoped>
.button-style {
    font-family: 'SCoreDream500';
    font-size: 0.9rem;
    transition: color 0.3s ease;
    border-radius: 0.4rem;
    background-color: #f0f0ff;
    color: #3868ff;
    align-items: center;
    /* position: absolute; */
    display: flex;
    justify-content: center;
    border: 1px solid #3868ff;
}

.button-style:hover {
    background-color: #dddee0;
}

.button-style:focus {
    outline: none;
}

.schedule-popup-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1002;
}

.schedule-popup-content {
    background: white;
    padding: 2rem;
    border-radius: 0.5rem;
    width: 100%;
    max-width: 35rem;
    display: flex;
    flex-direction: column;
    gap: 1rem;
}

.popup-title {
    font-family: 'SCoreDream600';
    font-size: 1.2rem;
    color: #333;
    text-align: center;
    margin: 0;
}

.summary-section {
    border: 1px solid #e0e0e0;
    border-radius: 0.4rem;
    padding: 0.5rem;
    background-color: #f9f9f9;
}

.summary-table {
    width: 100%;
    border-collapse: collapse;
}

.summary-table th,
.summary-table td {
    padding-left: 1rem;
    padding-right: 1rem;
    font-size: 0.95rem;
}

.summary-table th {
    text-align: left;
    font-family: 'SCoreDream500';
    color: #555;
    width: 120px;
}

.summary-table td {
    text-align: right;
    font-family: 'SCoreDream400';
    color: #111;
}

.schedule-container {
    max-height: 40vh;
    overflow-y: auto;
    border: 1px solid #e0e0e0;
    border-radius: 0.4rem;
}

.schedule-grid {
    width: 100%;
    border-collapse: collapse;
    text-align: center;
}

.schedule-grid th,
.schedule-grid td {
    padding: 0.75rem 0.5rem;
    border-bottom: 1px solid #eee;
    font-size: 0.9rem;
}

.schedule-grid thead {
    position: sticky;
    top: 0;
    background-color: #eee;
}

.schedule-grid th {
    font-family: 'SCoreDream400';
}

.schedule-grid tbody tr:last-child td {
    border-bottom: none;
}
.schedule-grid tbody tr:nth-child(even) {
    background-color: #fafafa;
}

.close-button {
    font-family: 'SCoreDream500';
    font-size: 0.9rem;
    background: #f1f1f1;
    padding: 0.5rem 1rem;
    border: none;
    border-radius: 0.25rem;
    cursor: pointer;
}

.close-button:hover {
    background: #ddd;
}
</style>
